/* eslint-disable @typescript-eslint/no-magic-numbers,@typescript-eslint/no-explicit-any */
import { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios';
import { StorageInterface } from '@/modules/backend/StorageInterface';
import { JsonResponse } from '@/modules/backend/Common/JsonResponse';
import { plainToClass } from 'class-transformer';
import { PermissionDeniedError } from '@/modules/backend/PermissionDeniedError';

const MSG_SERVER_ERROR = 'Server Error';

export default class RestService {
  public OnError: (message: string) => Promise<void>|void;
  public OnLogout: () => Promise<void>|void;
  public OnLogin: () => Promise<void>|void;

  private readonly axios: AxiosInstance;
  private readonly host: string = '';
  private readonly storage: StorageInterface;

  public constructor(axios: AxiosInstance, host: string, storage: StorageInterface) {
    this.axios = axios;
    this.storage = storage;
    this.host = host;
    this.OnError = (message): void => {
      console.log('request error', message);
    };
    this.OnLogout = (): void => {
      // Do nothing
    };
    this.OnLogin = (): void => {
      // Do nothing
    };

    this.axios.interceptors.response.use((response: any) => {
      return response;
    }, (e: any) => {
      if (e.response.status === 200) {
        return Promise.reject(new Error(e.response.data.message));
      }
      return Promise.reject(new Error('Неизвестная ошибка'));
    });
  }

  public async EmitError(message: string): Promise<void> {
    this.OnError(message);
  }

  public async EmitLogout(): Promise<void> {
    this.OnLogout();
  }

  public async EmitLogin(): Promise<void> {
    this.OnLogin();
  }

  public CheckOldToken(): void {
    const authToken = this.storage.get('AuthToken');
    const refreshTokenInsideAuth = authToken?.RefreshToken;
    if (typeof refreshTokenInsideAuth !== 'undefined') {
      this.storage.remove('AuthToken');
    }
  }

  public get IsAuth(): boolean {
    return this.storage.get('AuthToken') !== null;
  }

  public async request<T = any>(url: string, config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const headers = {} as any;
    const token = this.storage.get('AuthToken');

    if (token) {
      headers.Authorization = token.token;
    }

    return await this.axios.request({
      url,
      ...config,
      headers,
    });
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const headers = {} as any;

      const token = this.storage.get('AuthToken');

      if (token) {
        headers.Authorization = token.token;
      }

      const response = await this.axios.post(this.host + url, data, {
        ...config,
        headers
      });

      const jsonResponse = plainToClass(JsonResponse, response.data) as JsonResponse<T>;

      if (response.status !== 200) {
        throw new Error(MSG_SERVER_ERROR);
      }

      if (!jsonResponse.isSuccess) {
        throw new Error(jsonResponse.message);
      }

      return jsonResponse.data;
    } catch (e) {
      this.OnError(e.message);
      throw e;
    }
  }

  public async get<T = any>(url: string, config?: AxiosRequestConfig, targetClass?: any): Promise<T> {
    try {
      const headers = {} as any;

      const authToken = this.storage.get('AuthToken');

      if (authToken) {
        headers.Authorization = authToken.token;
      }

      const response = await this.axios.get(this.host + url, {
        ...config,
        headers
      });

      const jsonResponse = plainToClass(JsonResponse, response.data) as JsonResponse<T>;

      if ((jsonResponse.data as { name?: string })?.name === 'PermissionDeniedException') {
        throw new PermissionDeniedError();
      }

      if (response.status !== 200) {
        throw new Error(MSG_SERVER_ERROR);
      }

      if (!jsonResponse.isSuccess) {
        throw new Error(jsonResponse.message);
      }

      if (targetClass) {
        return plainToClass(targetClass, jsonResponse.data) as T;
      }

      return jsonResponse.data;
    } catch (e) {
      if (!e.name || e.name !== 'PermissionDeniedError') {
        this.OnError(e.message);
      }

      throw e;
    }
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const headers = {} as any;

      const token = this.storage.get('AuthToken');

      if (token) {
        headers.Authorization = token.token;
      }

      const response = await this.axios.put(this.host + url, data, {
        ...config,
        headers
      });

      const jsonResponse = plainToClass(JsonResponse, response.data) as JsonResponse<T>;

      if (response.status !== 200) {
        throw new Error(MSG_SERVER_ERROR);
      }

      if (!jsonResponse.isSuccess) {
        throw new Error(jsonResponse.message);
      }

      return jsonResponse.data;
    } catch (e) {
      this.OnError(e.message);
      throw e;
    }
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const headers = {} as any;

      const token = this.storage.get('AuthToken');

      if (token) {
        headers.Authorization = token.token;
      }

      const response = await this.axios.patch(this.host + url, data, {
        ...config,
        headers
      });

      const jsonResponse = plainToClass(JsonResponse, response.data) as JsonResponse<T>;

      if (response.status !== 200) {
        throw new Error(MSG_SERVER_ERROR);
      }

      if (!jsonResponse.isSuccess) {
        throw new Error(response.data.message);
      }

      return jsonResponse.data;
    } catch (e) {
      this.OnError(e.message);
      throw e;
    }
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const headers = {} as any;

      const token = this.storage.get('AuthToken');

      if (token) {
        headers.Authorization = token.token;
      }

      const response = await this.axios.delete(this.host + url, {
        ...config,
        headers
      });

      const jsonResponse = plainToClass(JsonResponse, response.data) as JsonResponse<T>;

      if (response.status !== 200) {
        throw new Error(MSG_SERVER_ERROR);
      }

      if (!jsonResponse.isSuccess) {
        throw new Error(response.data.message);
      }

      return jsonResponse.data;
    } catch (e) {
      this.OnError(e.message);
      throw e;
    }
  }
}
